import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './BnmMeasurementInfo.css';
import '../../App.css';
import { CSVLink } from 'react-csv';
import { ko } from 'date-fns/locale';
import config from '../../config';
import { findSeqIndex } from '../../utils/seqDataUtils';

const BnmMeasurementInfo = ({ SelJb }) => {
  const Jb_list = useSelector((state) => state.Jb_list || {});
  const JbMrtData = useSelector((state) => state.JbMrtData || {});
  // console.log(JbMrtData);

  const [parentListIndex, setParentListIndex] = useState(null);

  useEffect(() => {
    if (Jb_list.seq && Array.isArray(Jb_list.seq)) {
      const foundIndex = Jb_list.seq.findIndex((seq) => SelJb === seq);
      setParentListIndex(foundIndex !== -1 ? foundIndex : null);
    }
  }, [Jb_list, SelJb]);

  const parentDataIndex = useMemo(
    () => findSeqIndex(JbMrtData, SelJb),
    [JbMrtData, SelJb]
  );

  // 다수의 LC/CO 자식 지원: 부모 SelJb를 바라보는 모든 자식 인덱스 수집
  const { lcChildren, coChildren } = useMemo(() => {
    const seqArr = Jb_list.seq || [];
    const protoArr = Jb_list.jb_protocol || [];
    const addrArr = Jb_list.jb_address || Jb_list.address || [];
    const pairArr = (Jb_list.pair_device || Jb_list.pair_seq) || [];
    const lcTemp = [];
    const coTemp = [];
    for (let i = 0; i < seqArr.length; i++) {
      if (String(pairArr[i]) === String(SelJb)) {
        const addr = parseInt(addrArr[i] ?? 0, 10) || 0;
        const child = {
          listIndex: i,
          dataIndex: findSeqIndex(JbMrtData, seqArr[i]),
          addr,
          seq: seqArr[i],
        };
        if (protoArr[i] === 'Bnm_lc') lcTemp.push(child);
        if (protoArr[i] === 'Bnm_co') coTemp.push(child);
      }
    }
    // 백엔드와 동일 정책: 주소 오름차순, 동일시 seq 오름차순 정렬
    lcTemp.sort((a, b) => (a.addr - b.addr) || ((parseInt(a.seq, 10) || 0) - (parseInt(b.seq, 10) || 0)));
    coTemp.sort((a, b) => (a.addr - b.addr) || ((parseInt(a.seq, 10) || 0) - (parseInt(b.seq, 10) || 0)));
    return { lcChildren: lcTemp, coChildren: coTemp };
  }, [Jb_list, JbMrtData, SelJb]);

  const activeChannels = useMemo(() => {
    const channelUseArray =
      Jb_list.jb_pv_ch_use && parentListIndex != null && Jb_list.jb_pv_ch_use[parentListIndex]
        ? Jb_list.jb_pv_ch_use[parentListIndex].split(',')
        : [];
    const channelNameArray =
      Jb_list.jb_pv_ch_name && parentListIndex != null && Jb_list.jb_pv_ch_name[parentListIndex]
        ? Jb_list.jb_pv_ch_name[parentListIndex].split(',')
        : [];

    return channelUseArray
      .map((use, i) => (use === '1' ? { name: channelNameArray[i], index: i } : null))
      .filter((channel) => channel !== null);
  }, [Jb_list, parentListIndex]);

  const cellStyle = {
    minWidth: '150px',
    width: '150px',
    padding: '8px',
    textAlign: 'center',
    verticalAlign: 'middle',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  };

  // 요약 표에서 표시할 대표 CO 인덱스(첫 번째 CO 자식)
  const selectedCoChild = useMemo(() => (coChildren && coChildren.length > 0 ? coChildren[0] : null), [coChildren]);
  const selectedCoDataIndex = selectedCoChild?.dataIndex ?? -1;

  // LC 값 → 채널 인덱스(0..31)로 매핑 (첫 번째 LC: CH1~4, 두 번째 LC: CH5~8 ...)
  const { leakCurrentByChannel, leakSettingByChannel, relayByChannel } = useMemo(() => {
    const leakCurrentByChannel = Array(32).fill(null);
    const leakSettingByChannel = Array(32).fill(null);
    const relayByChannel = Array(32).fill(null);
    if (Array.isArray(lcChildren)) {
      lcChildren.forEach(({ dataIndex: childDataIndex }, lcOrder) => {
        if (childDataIndex === -1) return;
        const base = lcOrder * 4; // 0,4,8,...
        for (let k = 0; k < 4; k++) {
          const globalCh = base + k;
          if (globalCh >= 32) break;
          const lcCurr = JbMrtData.leakCurrent?.[childDataIndex]?.[k];
          const lcSet = JbMrtData.leakSetting?.[childDataIndex]?.[k];
          const lcRel = JbMrtData.relayStatus?.[childDataIndex]?.[k];
          leakCurrentByChannel[globalCh] = (lcCurr !== undefined && lcCurr !== null) ? lcCurr : null;
          leakSettingByChannel[globalCh] = (lcSet !== undefined && lcSet !== null) ? lcSet : null;
          relayByChannel[globalCh] = (lcRel !== undefined && lcRel !== null) ? lcRel : null;
        }
      });
    }
    return { leakCurrentByChannel, leakSettingByChannel, relayByChannel };
  }, [lcChildren, JbMrtData.leakCurrent, JbMrtData.leakSetting, JbMrtData.relayStatus]);

  const renderRelayIcon = (value) => {
    if (value == null || value === '-') return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return num === 0 ? '🟢' : '🔴';
  };

  // 단위 표시 헬퍼: 값이 유효할 때만 단위를 붙임
  const withUnit = (val, unit) => {
    if (val === null || val === undefined) return '-';
    if (val === '-') return '-';
    return `${val} ${unit}`;
  };

  // ===== 그래프/CSV 영역 (동일 포맷) =====
  const timeLabels = useMemo(
    () => [
      '05:00','05:10','05:20','05:30','05:40','05:50','06:00','06:10','06:20','06:30','06:40','06:50','07:00','07:10','07:20','07:30','07:40','07:50','08:00','08:10','08:20','08:30','08:40','08:50','09:00','09:10','09:20','09:30','09:40','09:50','10:00','10:10','10:20','10:30','10:40','10:50','11:00','11:10','11:20','11:30','11:40','11:50','12:00','12:10','12:20','12:30','12:40','12:50','13:00','13:10','13:20','13:30','13:40','13:50','14:00','14:10','14:20','14:30','14:40','14:50','15:00','15:10','15:20','15:30','15:40','15:50','16:00','16:10','16:20','16:30','16:40','16:50','17:00','17:10','17:20','17:30','17:40','17:50','18:00','18:10','18:20','18:30','18:40','18:50','19:00','19:10','19:20','19:30','19:40','19:50','20:00',
    ],
    []
  );

  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hiddenState, setHiddenState] = useState({});
  const [chartDataByTime, setChartDataByTime] = useState({});
  const chartRef = useRef(null);
  const [PvTotalVolt, setPvTotalVolt] = useState(Array(timeLabels.length).fill(0));
  const [PvTotalCurrent, setPvTotalCurrent] = useState(Array(timeLabels.length).fill(0));
  const [PvMaxTemp, setPvMaxTemp] = useState(Array(timeLabels.length).fill(0));
  const [PvFire, setPvFire] = useState(Array(timeLabels.length).fill('N/A'));

  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  const isWithinTimeRange = useCallback((createdAt, targetTime) => {
    const [targetHours, targetMinutes] = targetTime.split(':').map(Number);
    const dateObj = new Date(createdAt);
    const itemHours = dateObj.getUTCHours();
    const itemMinutes = dateObj.getUTCMinutes();
    const startTime = targetHours * 60 + targetMinutes;
    const endTime = startTime + 10;
    const itemTimeMinutes = itemHours * 60 + itemMinutes;
    return itemTimeMinutes >= startTime && itemTimeMinutes < endTime;
  }, []);

  const findClosestTimeData = useCallback(
    (filteredData, targetTime, channelIndex, seq) => {
      const closestData = filteredData.find(
        (item) => isWithinTimeRange(item.created_at, targetTime) && item.seq === seq
      );
      if (!closestData) {
        return {
          value: Array(32).fill(0),
          item: null,
          PvTotalVolt: 'N/A',
          PvTotalCurrent: 'N/A',
          maxTemp: 'N/A',
          fire: 'N/A',
        };
      }
      const pvChCurrentArray = closestData.PvChCurrent
        ? closestData.PvChCurrent.split(',').map(Number)
        : Array(32).fill(0);
      return {
        value: pvChCurrentArray,
        item: closestData,
        PvTotalVolt: closestData.PvTotalVolt || 'N/A',
        PvTotalCurrent: closestData.PvTotalCurrent || 'N/A',
        maxTemp: (closestData.maxTemp !== undefined ? closestData.maxTemp : 'N/A'),
        fire: closestData.fire !== undefined ? closestData.fire : 'N/A',
      };
    },
    [isWithinTimeRange]
  );

  const fetchData = useCallback(
    async (date) => {
      if (parentListIndex == null) return;
      setIsLoading(true);
      try {
        const selectedDateInKST = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
          .toISOString()
          .split('T')[0];
        const response = await fetch(`${config.backendUrl}/get_hourly_jb_mrt_data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: selectedDateInKST }),
        });
        const result = await response.json();
        if (!result.data || result.data.length === 0) {
          setEmptyData();
          return;
        }

        const filtered = result.data;
        const chartData = {};
        activeChannels.forEach((channel) => {
          chartData[channel.index] = [];
        });

        const newPvTotalVolt = Array(timeLabels.length).fill('N/A');
        const newPvTotalCurrent = Array(timeLabels.length).fill('N/A');
        const newPvMaxTemp = Array(timeLabels.length).fill('N/A');
        const newPvFire = Array(timeLabels.length).fill('N/A');

        timeLabels.forEach((label, timeIndex) => {
          activeChannels.forEach((channel) => {
            const { value: pvChCurrentArray, PvTotalVolt, PvTotalCurrent, maxTemp, fire } = findClosestTimeData(
              filtered,
              label,
              channel.index,
              SelJb
            );
            chartData[channel.index][timeIndex] = pvChCurrentArray[channel.index] || null;
            newPvTotalVolt[timeIndex] = PvTotalVolt;
            newPvTotalCurrent[timeIndex] = PvTotalCurrent;
            newPvMaxTemp[timeIndex] = maxTemp;
            newPvFire[timeIndex] = fire;
          });
        });

        setChartDataByTime(chartData);
        setPvTotalVolt(newPvTotalVolt);
        setPvTotalCurrent(newPvTotalCurrent);
        setPvMaxTemp(newPvMaxTemp);
        setPvFire(newPvFire);
        setIsDataAvailable(true);
      } catch (error) {
        setEmptyData();
      } finally {
        setIsLoading(false);
      }
    },
    [SelJb, activeChannels, findClosestTimeData, parentListIndex, timeLabels]
  );

  const setEmptyData = useCallback(() => {
    const emptyChartData = {};
    activeChannels.forEach((channel) => {
      emptyChartData[channel.index] = Array(timeLabels.length).fill(null);
    });
    setChartDataByTime(emptyChartData);
    setPvTotalVolt(Array(timeLabels.length).fill('N/A'));
    setPvTotalCurrent(Array(timeLabels.length).fill('N/A'));
    setPvMaxTemp(Array(timeLabels.length).fill('N/A'));
    setPvFire(Array(timeLabels.length).fill('N/A'));
    setIsDataAvailable(false);
  }, [activeChannels, timeLabels]);

  useEffect(() => {
    if (parentListIndex != null) {
      fetchData(selectedDate);
    }
  }, [fetchData, selectedDate, parentListIndex]);

  useEffect(() => {
    const intervalId = setInterval(() => fetchData(new Date()), 660000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const generateCSVData = () => {
    const csvData = [];
    timeLabels.forEach((label, idx) => {
      const row = {
        time: label,
        PvTotalVolt: PvTotalVolt[idx],
        PvTotalCurrent: PvTotalCurrent[idx],
        Temperature: PvMaxTemp[idx],
        Fire: PvFire[idx],
      };
      activeChannels.forEach((channel) => {
        row[`Channel ${channel.index} Current`] = chartDataByTime[channel.index]?.[idx] || null;
      });
      csvData.push(row);
    });
    return csvData;
  };

  const csvHeaders = [
    { label: 'Time', key: 'time' },
    { label: 'PvTotalVolt', key: 'PvTotalVolt' },
    { label: 'PvTotalCurrent', key: 'PvTotalCurrent' },
    { label: 'Temperature', key: 'Temperature' },
    { label: 'Fire', key: 'Fire' },
    ...activeChannels.map((channel) => ({
      label: `Channel ${channel.index} Current`,
      key: `Channel ${channel.index} Current`,
    })),
  ];

  const chartData = {
    labels: timeLabels,
    datasets: activeChannels.map((channel, i) => ({
      label: channel.name,
      data: chartDataByTime[channel.index]?.map((value) => (value === 0 ? null : value)) || [],
      borderColor: [
        '#EF5350','#26C6DA','#FF7043','#AB47BC','#FFCA28','#3A736A','#7E57C2','#60A07A','#FFA726','#8D6E63','#66BB6A','#D44A46','#7F8C8D','#4B4B3D','#918D62','#DAB672','#F1D597','#F4B381','#F19557','#E05D44','#C25054','#AC6D82','#26A69A','#6B4E3D',
      ][i % 24],
      borderWidth: 1,
      fill: false,
      tension: 0.4,
      hidden: hiddenState[channel.index] || false,
      pointRadius: chartDataByTime[channel.index]?.map((value) => (value === null ? 0 : 1)),
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        display: true,
        title: { display: true, text: '전류 (A)' },
        min: 0,
        ticks: {
          callback: function (value) { return (value / 10).toFixed(1); },
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: { boxWidth: 15, padding: 15, font: { size: 12 }, usePointStyle: true },
        onClick: (e, legendItem) => {
          const dIdx = legendItem.datasetIndex;
          const channelIndex = activeChannels[dIdx].index;
          setHiddenState((prev) => ({ ...prev, [channelIndex]: !prev[channelIndex] }));
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        position: 'nearest',
        yAlign: 'center',
        xAlign: 'right',
        caretPadding: 30,
        caretSize: 10,
        callbacks: {
          title: (items) => {
            const dataIndex = items[0].dataIndex;
            const timeLabel = timeLabels[dataIndex];
            const Pvvolt = PvTotalVolt[dataIndex] || 'N/A';
            const Pvcurrent = PvTotalCurrent[dataIndex] || 'N/A';
            const temperature = PvMaxTemp[dataIndex] || 'N/A';
            const fire = PvFire[dataIndex] === 1 ? '화재 발생' : PvFire[dataIndex] === 0 ? '화재 없음' : 'N/A';
            return [
              `시간: ${timeLabel}`,
              `통합전압: ${(Pvvolt / 10).toFixed(1)}V`,
              `통합전류: ${(Pvcurrent / 10).toFixed(1)}A`,
              `온도: ${(temperature / 10).toFixed(1)}℃`,
              `화재: ${fire}`,
            ];
          },
          label: (tooltipItem) => {
            const channelIndex = activeChannels[tooltipItem.datasetIndex]?.index;
            if (channelIndex === undefined || !chartDataByTime[channelIndex]) return '';
            const value = chartDataByTime[channelIndex][tooltipItem.dataIndex] || 0;
            return [`${tooltipItem.dataset.label}: ${(value / 10).toFixed(1)}A`];
          },
        },
        bodyFont: { size: 10 },
        titleFont: { size: 12 },
      },
    },
  };

  const handleSelectAll = () => {
    const allHiddenState = {};
    activeChannels.forEach((channel) => {
      allHiddenState[channel.index] = false;
    });
    setHiddenState(allHiddenState);
  };

  const handleDeselectAll = () => {
    const allHiddenState = {};
    activeChannels.forEach((channel) => {
      allHiddenState[channel.index] = true;
    });
    setHiddenState(allHiddenState);
  };

  if (parentListIndex === null) return <div style={{ padding: 12 }}>장비 로딩 중...</div>;

  return (
    <div>
      <div style={{ margin: '10px', maxWidth: '1560px' }}>
        <div className="JbGenTable">
          <div className="summary-table-container">
            <table className="summary-table" style={{ marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <th style={{ backgroundColor: '#3D98DE' }}>통합전압</th>
                  <th style={{ backgroundColor: '#3D98DE' }}>통합전류</th>
                  <th style={{ backgroundColor: '#3D98DE' }}>통합전력</th>
                  <th style={{ backgroundColor: '#3D98DE' }}>접속함 내부 온도</th>
                  <th style={{ backgroundColor: '#3D98DE' }}>CO 가스 측정값</th>
                  <th style={{ backgroundColor: '#3D98DE' }}>CO 가스 경보 설정값</th>
                  <th style={{ backgroundColor: '#3D98DE' }}>내부 온도 경보 설정값</th>
                </tr>
                <tr>
                  <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{(((JbMrtData.PvTotalVolt?.[parentDataIndex] ?? 0) / 10)).toFixed(1)} V</td>
                  <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{(((JbMrtData.pvTotalCurrent?.[parentDataIndex] ?? 0) / 10)).toFixed(1)} A</td>
                  <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{((((JbMrtData.PvTotalVolt?.[parentDataIndex] ?? 0) * (JbMrtData.pvTotalCurrent?.[parentDataIndex] ?? 0)) / 100000)).toFixed(2)} kW</td>
                  <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{
                    selectedCoDataIndex >= 0
                      ? (((JbMrtData.maxTemp?.[selectedCoDataIndex] ?? 0) / 10)).toFixed(1) + ' ℃'
                      : '-'
                  }</td>
                  <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{
                    selectedCoDataIndex >= 0 ? withUnit(JbMrtData.coValue?.[selectedCoDataIndex], 'PPM') : '-'
                  }</td>
                  <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{
                    selectedCoDataIndex >= 0 ? withUnit(JbMrtData.coAlarmSetting?.[selectedCoDataIndex], 'PPM') : '-'
                  }</td>
                  <td style={{ fontSize: '18px', fontWeight: 'bold' }}>{
                    selectedCoDataIndex >= 0
                      ? (((JbMrtData.tempAlarmSetting?.[selectedCoDataIndex] ?? 0) / 10)).toFixed(1) + ' ℃'
                      : '-'
                  }</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="Bni-table-container" style={{ marginTop: '-15px' }}>
            <table className="Bni-channel-info-table">
              <thead>
                <tr>
                  <th style={cellStyle}>{Jb_list.jb_name[parentListIndex]}</th>
                  {activeChannels.map((channel) => (
                    <th key={channel.index}>{channel.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>전압 (V)</td>
                  {activeChannels.map((channel, i) => (
                    <td key={`volt-${channel.index}`}>{(((JbMrtData.pvChVolt?.[parentDataIndex]?.[i] ?? 0) / 10)).toFixed(1)}</td>
                  ))}
                </tr>
                <tr>
                  <td>전류 (A)</td>
                  {activeChannels.map((channel, i) => (
                    <td key={`current-${channel.index}`}>{(((JbMrtData.pvChCurrent?.[parentDataIndex]?.[i] ?? 0) / 10)).toFixed(1)}</td>
                  ))}
                </tr>
                <tr>
                  <td>전력 (kW)</td>
                  {activeChannels.map((channel, i) => (
                    <td key={`power-${channel.index}`}>{((((JbMrtData.pvChVolt?.[parentDataIndex]?.[i] ?? 0) * (JbMrtData.pvChCurrent?.[parentDataIndex]?.[i] ?? 0)) / 100000)).toFixed(2)}</td>
                  ))}
                </tr>
                <tr>
                  <td>누설전류 (mA)</td>
                  {activeChannels.map((channel) => (
                    <td key={`leakcur-${channel.index}`}>{
                      leakCurrentByChannel?.[channel.index] ?? '-'
                    }</td>
                  ))}
                </tr>
                <tr>
                  <td>누설전류 셋팅값 (mA)</td>
                  {activeChannels.map((channel) => (
                    <td key={`leakset-${channel.index}`}>{
                      leakSettingByChannel?.[channel.index] ?? '-'
                    }</td>
                  ))}
                </tr>
                <tr>
                  <td>릴레이 동작</td>
                  {activeChannels.map((channel) => (
                  <td key={`relay-${channel.index}`}>{renderRelayIcon(relayByChannel?.[channel.index])}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* LC/CO 별도 블록 표시 제거 */}
        </div>
      </div>

      {/* 그래프 + 컨트롤 (동일 포맷) */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: '10px', padding: '12px', marginTop: '10px' }}>
        <div style={{ margin: '0 10px', maxWidth: '1560px' }}>
          <div
            className="control-container"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10px' }}
          >
            <button onClick={handleSelectAll} className="styled-button" style={{ marginRight: '5px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>전체 선택</button>
            <button onClick={handleDeselectAll} className="styled-button" style={{ height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>전체 해제</button>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="yyyy-MM-dd"
              locale={ko}
              className="styled-datepicker"
              popperPlacement="bottom"
              maxDate={new Date()}
              customInput={<input style={{ textAlign: 'center', height: '25px', lineHeight: '25px', width: '150px', marginRight: '10px' }} />}
            />
            <CSVLink
              data={generateCSVData()}
              headers={csvHeaders}
              filename={`data_${selectedDateStr}.csv`}
              className="styled-button"
              style={{ marginLeft: '20px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', backgroundColor: '#1D6C41' }}
            >
              CSV 다운로드
            </CSVLink>
          </div>

          <div style={{ height: '320px', paddingTop: '0px' }}>
            {isLoading ? (
              <div className="loading-message">데이터를 불러오는 중...</div>
            ) : !isDataAvailable ? (
              <div className="no-data-message">데이터가 없습니다.</div>
            ) : (
              <Line ref={chartRef} data={chartData} options={chartOptions} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BnmMeasurementInfo;
